/**
 * OBS Diagnostics Panel
 *
 * Карточки на табе OBS на странице трансляции:
 *  - Test Mode toggle (выключатель «Тестовый режим»)
 *  - Diagnostics card (status badge + чек-лист + распарсенные warning'и)
 *  - Raw logs card (collapsible tail JSONL)
 *
 * Polling диагностики каждые 5 сек пока эфир активен (source connected или
 * status LIVE). После остановки эфира оставляем последний снимок.
 */

import { useCallback, useState } from 'react';
import { useCustom, useCustomMutation, useInvalidate } from '@refinedev/core';
import { notify } from '@/lib/notify';
import {
  Activity,
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  CircleHelp,
  RefreshCw,
  Terminal,
  X,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { resolveApiUrl } from '@/providers/api-url';

interface DiagnosticsCheck {
  id: string;
  label: string;
  severity: 'ok' | 'warn' | 'error' | 'unknown';
  expected: string;
  actual: string;
  hint: string | null;
}

interface DiagnosticsWarning {
  kind: string;
  severity: 'info' | 'warn' | 'error';
  count: number;
  firstSeenAt: string;
  lastSeenAt: string;
  sample: string;
  cause: string;
  fix: string;
}

interface DiagnosticsSource {
  connected: boolean;
  readyTime: string | null;
  videoCodec: string | null;
  videoProfile: string | null;
  videoLevel: string | null;
  resolution: { width: number; height: number } | null;
  audioCodec: string | null;
  audioSampleRate: number | null;
  audioChannels: number | null;
  bitrateMbps: number | null;
}

interface ObsDiagnosticsDTO {
  status: 'offline' | 'ok' | 'warning' | 'error';
  source: DiagnosticsSource;
  checks: DiagnosticsCheck[];
  warnings: DiagnosticsWarning[];
  rawLogAvailable: boolean;
  testMode: boolean;
  snapshotAt: string;
}

interface RawLogLine {
  ts: string;
  level: string;
  message: string;
}

interface RawLogsDTO {
  lines: RawLogLine[];
  truncated: boolean;
  available: boolean;
  filePath: string;
}

interface Props {
  livestreamId: number;
  livestreamStatus: string;
  testMode: boolean;
  onTestModeChange: () => void;
}

const apiBase = resolveApiUrl().replace(/\/$/, '');

export function ObsDiagnosticsPanel({ livestreamId, livestreamStatus, testMode, onTestModeChange }: Props) {
  const isLive = livestreamStatus === 'LIVE' || livestreamStatus === 'RECONNECTING';
  const canToggleTestMode = livestreamStatus === 'SCHEDULED';

  const diagnosticsUrl = `${apiBase}/admin/livestreams/${livestreamId}/obs-diagnostics`;
  const queryResult = useCustom<ObsDiagnosticsDTO>({
    url: diagnosticsUrl,
    method: 'get',
    queryOptions: {
      // Реалтайм: cron шлёт livestream.obs.diagnostics каждые 4с пока
      // LIVE/RECONNECTING или SCHEDULED+testMode. refetchInterval — safety-net.
      refetchInterval: isLive || testMode ? 5 * 60_000 : false,
    },
  });
  const { result, query } = queryResult;
  const isFetching = query.isFetching;
  const isError = query.isError;
  const diagnostics = result?.data as ObsDiagnosticsDTO | undefined;
  const invalidate = useInvalidate();

  const refetchDiagnostics = useCallback(() => {
    void invalidate({ resource: 'livestreams', invalidates: ['all'] });
  }, [invalidate]);

  const { mutate: toggleTestMode, mutation: toggleMutation } = useCustomMutation();
  const isToggling = toggleMutation.isPending;

  const handleToggle = useCallback(
    (next: boolean) => {
      toggleTestMode(
        {
          url: `${apiBase}/admin/livestreams/${livestreamId}/test-mode`,
          method: 'patch',
          values: { enabled: next },
        },
        {
          onSuccess: () => {
            notify.success(next ? 'Тестовый режим включён' : 'Тестовый режим выключен');
            onTestModeChange();
            refetchDiagnostics();
          },
          onError: (error) => {
            notify.error(error instanceof Error ? error.message : 'Не удалось переключить тестовый режим');
          },
        },
      );
    },
    [livestreamId, onTestModeChange, refetchDiagnostics, toggleTestMode],
  );

  return (
    <div className="space-y-4">
      <TestModeCard
        enabled={testMode}
        canToggle={canToggleTestMode}
        loading={isToggling}
        onToggle={handleToggle}
      />

      <DiagnosticsCard
        diagnostics={diagnostics}
        isFetching={isFetching}
        isError={isError}
        errorMessage={query.error instanceof Error ? query.error.message : null}
        onRefresh={refetchDiagnostics}
      />

      <RawLogsCard livestreamId={livestreamId} available={diagnostics?.rawLogAvailable ?? false} />
    </div>
  );
}

// ============= Test Mode Card =============

interface TestModeCardProps {
  enabled: boolean;
  canToggle: boolean;
  loading: boolean;
  onToggle: (next: boolean) => void;
}

function TestModeCard({ enabled, canToggle, loading, onToggle }: TestModeCardProps) {
  return (
    <Card className={enabled ? 'border-amber-500/60 bg-amber-500/5' : ''}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Тестовый режим
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              {enabled
                ? 'Эфир принимается MediaMTX для проверки настроек OBS, но НЕ виден зрителям. Уведомления и broadcasts не отправляются.'
                : 'Включите перед стартом OBS чтобы проверить настройки без риска показать тестовую картинку зрителям. Доступно только пока трансляция SCHEDULED.'}
            </CardDescription>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={onToggle}
            disabled={!canToggle || loading}
            aria-label="Тестовый режим"
          />
        </div>
      </CardHeader>
      {!canToggle && !enabled && (
        <CardContent className="pt-0">
          <p className="text-xs text-muted-foreground">
            Тестовый режим нельзя включить после старта эфира — иначе зрители уже идущей трансляции потеряют поток.
          </p>
        </CardContent>
      )}
    </Card>
  );
}

// ============= Diagnostics Card =============

interface DiagnosticsCardProps {
  diagnostics: ObsDiagnosticsDTO | undefined;
  isFetching: boolean;
  isError: boolean;
  errorMessage: string | null;
  onRefresh: () => void;
}

function DiagnosticsCard({ diagnostics, isFetching, isError, errorMessage, onRefresh }: DiagnosticsCardProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            Диагностика подключения
            {diagnostics && <StatusBadge status={diagnostics.status} />}
          </CardTitle>
          <Button type="button" variant="outline" size="sm" onClick={onRefresh} disabled={isFetching}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isFetching ? 'animate-spin' : ''}`} />
            Обновить
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isError ? (
          <div className="rounded-md border border-red-500/40 bg-red-500/5 p-3 text-sm">
            <p className="font-medium mb-1">Не удалось загрузить диагностику</p>
            <p className="text-xs text-muted-foreground">
              {errorMessage ?? 'Backend вернул ошибку. Проверьте логи backend и доступность MediaMTX API.'}
            </p>
          </div>
        ) : !diagnostics ? (
          <p className="text-xs text-muted-foreground">Загрузка...</p>
        ) : diagnostics.status === 'offline' ? (
          <div className="rounded-md border border-zinc-500/30 bg-zinc-500/5 p-3 text-sm">
            <p className="font-medium mb-1">OBS не подключён к серверу</p>
            <p className="text-xs text-muted-foreground">
              Запустите OBS с настроенным RTMP URL и Stream Key. Как только поток дойдёт до MediaMTX —
              на этом блоке появится живая диагностика.
            </p>
          </div>
        ) : (
          (() => {
            const checks = Array.isArray(diagnostics.checks) ? diagnostics.checks : [];
            const warnings = Array.isArray(diagnostics.warnings) ? diagnostics.warnings : [];
            const allOk = checks.length > 0 && checks.every((c) => c.severity === 'ok') && warnings.length === 0;
            return (
              <>
                {diagnostics.source && <SourceBlock source={diagnostics.source} />}
                {checks.length > 0 && <ChecksList checks={checks} />}
                {warnings.length > 0 && <WarningsList warnings={warnings} />}
                {allOk && (
                  <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3 text-sm flex items-start gap-2">
                    <Check className="h-4 w-4 mt-0.5 text-emerald-500 shrink-0" />
                    <span>Все настройки OBS корректны, предупреждений за текущий эфир нет.</span>
                  </div>
                )}
              </>
            );
          })()
        )}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }: { status: ObsDiagnosticsDTO['status'] }) {
  if (status === 'ok') {
    return <Badge variant="outline" className="border-emerald-500/60 text-emerald-700 dark:text-emerald-400">OK</Badge>;
  }
  if (status === 'warning') {
    return <Badge variant="outline" className="border-amber-500/60 text-amber-700 dark:text-amber-400">Внимание</Badge>;
  }
  if (status === 'error') {
    return <Badge variant="outline" className="border-red-500/60 text-red-700 dark:text-red-400">Ошибки</Badge>;
  }
  return <Badge variant="outline">Offline</Badge>;
}

function SourceBlock({ source }: { source: DiagnosticsSource }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-xs">
      <SourceRow label="Видео" value={source.videoCodec ? `${source.videoCodec}${source.videoProfile ? ` (${source.videoProfile})` : ''}` : '—'} />
      <SourceRow label="Разрешение" value={source.resolution ? `${source.resolution.width}×${source.resolution.height}` : '—'} />
      <SourceRow label="Битрейт" value={source.bitrateMbps !== null ? `${source.bitrateMbps} Mbps` : '—'} />
      <SourceRow label="Аудио" value={source.audioCodec ?? '—'} />
      <SourceRow label="Sample rate" value={source.audioSampleRate ? `${source.audioSampleRate} Hz` : '—'} />
      <SourceRow label="Каналы" value={source.audioChannels !== null ? String(source.audioChannels) : '—'} />
    </div>
  );
}

function SourceRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-muted-foreground">{label}</div>
      <div className="font-mono text-sm">{value}</div>
    </div>
  );
}

function ChecksList({ checks }: { checks: DiagnosticsCheck[] }) {
  return (
    <div>
      <div className="text-xs font-semibold mb-2 text-muted-foreground uppercase tracking-wide">
        Чек-лист настроек
      </div>
      <ul className="space-y-1.5">
        {checks.map((c) => (
          <li key={c.id} className="flex items-start gap-2 text-sm">
            <CheckIcon severity={c.severity} />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-medium">{c.label}:</span>
                <span className="font-mono">{c.actual}</span>
                <span className="text-xs text-muted-foreground">(ожидается: {c.expected})</span>
              </div>
              {c.hint && (
                <p className="text-xs text-muted-foreground mt-0.5">{c.hint}</p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CheckIcon({ severity }: { severity: DiagnosticsCheck['severity'] }) {
  if (severity === 'ok') {
    return <Check className="h-4 w-4 mt-0.5 text-emerald-500 shrink-0" />;
  }
  if (severity === 'warn') {
    return <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />;
  }
  if (severity === 'error') {
    return <X className="h-4 w-4 mt-0.5 text-red-500 shrink-0" />;
  }
  return <CircleHelp className="h-4 w-4 mt-0.5 text-zinc-400 shrink-0" />;
}

function WarningsList({ warnings }: { warnings: DiagnosticsWarning[] }) {
  return (
    <div>
      <div className="text-xs font-semibold mb-2 text-muted-foreground uppercase tracking-wide">
        Предупреждения MediaMTX за текущий эфир
      </div>
      <ul className="space-y-2">
        {warnings.map((w) => (
          <li
            key={w.kind}
            className={
              w.severity === 'error'
                ? 'rounded-md border-2 border-red-500/60 bg-red-500/10 p-3'
                : w.severity === 'warn'
                  ? 'rounded-md border-2 border-amber-500/60 bg-amber-500/10 p-3'
                  : 'rounded-md border border-zinc-500/40 bg-zinc-500/5 p-3'
            }
          >
            <div className="flex items-start gap-2">
              <WarningIcon severity={w.severity} />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-semibold text-sm">{warningTitle(w.kind)}</span>
                  <Badge variant="outline" className="text-xs">×{w.count}</Badge>
                </div>
                <p className="text-xs mt-1.5"><b>Причина.</b> {w.cause}</p>
                <p className="text-xs mt-1"><b>Решение.</b> {w.fix}</p>
                <p className="text-[11px] text-muted-foreground mt-1.5 font-mono break-all">
                  {w.sample}
                </p>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function WarningIcon({ severity }: { severity: DiagnosticsWarning['severity'] }) {
  if (severity === 'error') {
    return <X className="h-4 w-4 mt-0.5 text-red-500 shrink-0" />;
  }
  if (severity === 'warn') {
    return <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />;
  }
  return <CircleHelp className="h-4 w-4 mt-0.5 text-zinc-400 shrink-0" />;
}

function warningTitle(kind: string): string {
  const titles: Record<string, string> = {
    part_duration_drift: 'Дрейф длительности part',
    segment_duration_drift: 'Скачок длительности сегмента',
    muxer_error_dts_reorder: 'Ошибка DTS reorder',
    muxer_error_generic: 'Ошибка муксера',
    skipping_track: 'Пропущен трек (несовместимый кодек)',
    no_supported_codecs: 'Нет поддерживаемых кодеков',
    reader_too_slow: 'Зритель не успевает читать поток',
    rtmp_io_timeout: 'Сеть стримера: timeout',
    rtmp_conn_reset: 'Сеть стримера: обрыв TCP',
    bitrate_exceeded: 'Превышен лимит битрейта',
  };
  return titles[kind] ?? kind;
}

// ============= Raw Logs Card =============

function RawLogsCard({ livestreamId, available }: { livestreamId: number; available: boolean }) {
  const [open, setOpen] = useState(false);
  const { result, query } = useCustom<RawLogsDTO>({
    url: `${apiBase}/admin/livestreams/${livestreamId}/raw-logs`,
    method: 'get',
    queryOptions: {
      enabled: open,
      // Реалтайм: livestream.obs.diagnostics инвалидирует raw-logs тоже,
      // refetchInterval — safety-net пока диалог открыт.
      refetchInterval: open ? 5 * 60_000 : false,
    },
  });
  const isFetching = query.isFetching;
  const logs = result?.data as RawLogsDTO | undefined;
  const invalidate = useInvalidate();
  const refetchLogs = useCallback(() => {
    void invalidate({ resource: 'livestreams', invalidates: ['all'] });
  }, [invalidate]);

  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CardHeader className="pb-3">
          <CollapsibleTrigger asChild>
            <div className="flex items-center justify-between cursor-pointer">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Terminal className="h-4 w-4" />
                Сырые логи MediaMTX
              </CardTitle>
              <Button type="button" variant="ghost" size="sm">
                {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
          </CollapsibleTrigger>
          <CardDescription className="text-xs">
            {available
              ? 'Tail последних 500 строк JSONL с авто-обновлением каждые 3 сек. Используется для отладки случаев, не покрытых парсером.'
              : 'Лог-файл MediaMTX недоступен. Убедитесь что в mediamtx.yml включены logDestinations [stdout, file] и смонтирован volume mediamtx_logs.'}
          </CardDescription>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="space-y-3">
            {(() => {
              const lines = Array.isArray(logs?.lines) ? logs!.lines : [];
              return (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {lines.length} строк{logs?.truncated ? ' (обрезано)' : ''}
                    </span>
                    <Button type="button" variant="outline" size="sm" onClick={refetchLogs} disabled={isFetching}>
                      <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isFetching ? 'animate-spin' : ''}`} />
                      Обновить
                    </Button>
                  </div>
                  <pre className="max-h-96 overflow-auto rounded-md border bg-zinc-950 p-3 text-[11px] leading-relaxed font-mono text-zinc-100">
                    {lines.length
                      ? lines.map((l, i) => (
                    <div
                      key={`${l.ts}-${i}`}
                      className={
                        l.level === 'ERR' || l.level === 'ERROR'
                          ? 'text-red-400'
                          : l.level === 'WAR' || l.level === 'WARN'
                            ? 'text-amber-300'
                            : 'text-zinc-400'
                      }
                    >
                      <span className="text-zinc-600">{l.ts.slice(11, 19)}</span>{' '}
                      <span className="font-bold">{l.level}</span>{' '}
                      {l.message}
                    </div>
                  ))
                : <div className="text-zinc-500">Лог пуст или недоступен.</div>}
                  </pre>
                </>
              );
            })()}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
